import React, { useEffect, useState, useRef } from "react";
import { useAuthCheck } from "../components/Auth";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_ROUTES } from "../components/Api";
import Loading from "../components/Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faPenToSquare } from "@fortawesome/free-solid-svg-icons";

function ViewUnityCard() {
    const { id } = useParams();
    const [cardProduct, setCardProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const { checkToken } = useAuthCheck();

    // Modals
    const [showSellModal, setShowSellModal] = useState(false);
    const [showReplenishmentModal, setShowReplenishmentModal] = useState(false);
    const [showPriceModal, setShowPriceModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    // Vente / Réappro / Prix
    const [newBuyPrice, setNewBuyPrice] = useState("");
    const [sellQty, setSellQty] = useState(0);
    const [sellPrice, setSellPrice] = useState("");
    const [sellQualityId, setSellQualityId] = useState("");
    const [replenishQty, setReplenishQty] = useState(0);
    const [replenishQualityId, setReplenishQualityId] = useState("");

    // Duplication
    const [licenses, setLicenses] = useState([]);
    const [collections, setCollections] = useState([]);
    const [extensions, setExtensions] = useState([]);
    const [rarities, setRarities] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [qualities, setQualities] = useState([]);
    const [selectedLicense, setSelectedLicense] = useState("");
    const [filteredCollections, setFilteredCollections] = useState([]);
    const [filteredExtensions, setFilteredExtensions] = useState([]);

    const [dupForm, setDupForm] = useState({
        name: "",
        reference: "",
        booster: false,
        buy: "",
        sell: "",
        commentary: "",
        license_card_id: "",
        collection_id: "",
        extension_id: "",
        rarity_card_id: "",
        language_id: "",
    });

    useEffect(() => { checkToken(); }, [checkToken]);

    // Fetch la carte
    useEffect(() => {
        const fetchCard = async () => {
            setLoading(true);
            try {
                const res = await api.get(`${API_ROUTES.UNITYCARDS}/${id}`);
                const data = res.data;

                const licensesRes = await api.get(API_ROUTES.LICENSECARDS);
                const license = licensesRes.data.find(l => l.id === data.license_card_id);
                if (license) data.license_name = license.license_card;

                setCardProduct(data);
                setNewBuyPrice(data.buy || "");
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchCard();
    }, [id]);

    const openFilePicker = () => fileInputRef.current?.click();

    const handleUploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("picture", file);

        try {
            const res = await api.post(`${API_ROUTES.UNITYCARDS}/${id}/photo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setCardProduct(res.data);
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert("Échec de l’upload de l’image.");
        } finally {
            e.target.value = "";
        }
    };

    const updateBuyPrice = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post(`${API_ROUTES.UNITYCARDS}/${id}/buy`, {
                buy: parseFloat(newBuyPrice)
            });
            setCardProduct(res.data);
            setShowPriceModal(false);
            alert("Prix d’achat mis à jour !");
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la mise à jour du prix d’achat.");
        }
    };

    const sellCards = async (e) => {
        e.preventDefault();
        if (!sellQualityId) return alert("Veuillez sélectionner une qualité");
        if (!sellQty || sellQty <= 0) return alert("Veuillez saisir une quantité valide");
        if (!sellPrice || sellPrice <= 0) return alert("Veuillez saisir un prix de vente valide");

        try {
            const payload = { qualities: [{ quality_id: parseInt(sellQualityId), quantity: sellQty, sell: parseFloat(sellPrice) }] };
            await api.post(`${API_ROUTES.UNITYCARDS}/${id}/sell`, payload);
            const res = await api.get(`${API_ROUTES.UNITYCARDS}/${id}`);
            setCardProduct(res.data);
            setShowSellModal(false);
            setSellQty(0); setSellPrice(""); setSellQualityId("");
            alert("Vente effectuée !");
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la vente : " + (err.response?.data?.message || ""));
        }
    };

    const replenishmentCards = async (e) => {
        e.preventDefault();
        if (!replenishQualityId) return alert("Veuillez sélectionner une qualité");
        try {
            const res = await api.post(`${API_ROUTES.UNITYCARDS}/${id}/replenishment`, {
                quantity: replenishQty,
                quality_id: replenishQualityId
            });
            setCardProduct(res.data);
            setShowReplenishmentModal(false);
            setReplenishQty(0);
            setReplenishQualityId("");
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert("Erreur lors du réapprovisionnement");
        }
    };

    // --- Duplication ---
    useEffect(() => {
        if (!showDuplicateModal) return;
        const fetchData = async () => {
            try {
                setLoading(true);
                const [
                    licensesRes,
                    collectionsRes,
                    extensionsRes,
                    raritiesRes,
                    languagesRes,
                    qualitiesRes
                ] = await Promise.all([
                    api.get("/license-cards"),
                    api.get("/collections"),
                    api.get("/extensions"),
                    api.get("/rarity-cards"),
                    api.get("/languages"),
                    api.get("/quality-cards")
                ]);

                setLicenses(licensesRes.data);
                setCollections(collectionsRes.data);
                setExtensions(extensionsRes.data);
                setRarities(raritiesRes.data);
                setLanguages(languagesRes.data);
                setQualities(qualitiesRes.data);

                if (cardProduct) {
                    setDupForm({
                        name: cardProduct.name || "",
                        reference: cardProduct.reference || "",
                        booster: cardProduct.booster || false,
                        buy: cardProduct.buy || "",
                        sell: cardProduct.sell || "",
                        commentary: cardProduct.commentary || "",
                        license_card_id: cardProduct.license_card_id || "",
                        collection_id: cardProduct.collection_id || "",
                        extension_id: cardProduct.extension_id || "",
                        rarity_card_id: cardProduct.rarity_card_id || "",
                        language_id: cardProduct.language_id || "",
                    });
                    setSelectedLicense(cardProduct.license_card_id || "");
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [showDuplicateModal, cardProduct]);

    // Relations Licence → Collection → Extension
    useEffect(() => {
        if (selectedLicense) {
            const filtered = collections.filter(c => c.license_card_id === parseInt(selectedLicense));
            setFilteredCollections(filtered);
            setFilteredExtensions([]);
            setDupForm(prev => ({ ...prev, license_card_id: selectedLicense, collection_id: "", extension_id: "" }));
        } else {
            setFilteredCollections([]);
            setFilteredExtensions([]);
        }
    }, [selectedLicense, collections]);

    useEffect(() => {
        if (dupForm.collection_id) {
            const filtered = extensions.filter(ext => ext.collection_id === parseInt(dupForm.collection_id));
            setFilteredExtensions(filtered);
        } else {
            setFilteredExtensions([]);
        }
    }, [dupForm.collection_id, extensions]);

    const handleDupChange = (e) => {
        const { name, value, type } = e.target;
        const newValue = type === "radio" ? value === "true" : value;
        if (name === "booster") {
            setDupForm(prev => ({ ...prev, booster: newValue, rarity_card_id: newValue ? null : prev.rarity_card_id }));
        } else {
            setDupForm(prev => ({ ...prev, [name]: newValue }));
        }
    };

    const handleDupSubmit = async (e) => {
        e.preventDefault();
        const isBooster = dupForm.booster;
        if (!isBooster && !dupForm.rarity_card_id) return alert("Pour une carte unique, la rareté est obligatoire.");
        const qualitiesPayload = !isBooster ? qualities.map(q => ({ id: q.id, quantity: 0 })) : [];
        const payload = {
            license_card_id: parseInt(dupForm.license_card_id),
            collection_id: parseInt(dupForm.collection_id),
            extension_id: dupForm.extension_id ? parseInt(dupForm.extension_id) : null,
            language_id: dupForm.language_id ? parseInt(dupForm.language_id) : null,
            rarity_card_id: isBooster ? null : parseInt(dupForm.rarity_card_id),
            name: dupForm.name,
            reference: dupForm.reference,
            booster: isBooster ? 1 : 0,
            buy: dupForm.buy ? parseFloat(dupForm.buy) : null,
            sell: dupForm.sell ? parseFloat(dupForm.sell) : null,
            commentary: dupForm.commentary,
            qualities: qualitiesPayload
        };
        try {
            const res = await api.post("/unity-cards", payload);
            setShowDuplicateModal(false);
            navigate(`/unity_card/${res.data.id}`);
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la duplication");
        }
    };

    // Rendu principal (inchangé sauf modale duplication)
    if (loading) return <Loading message="Chargement de la carte..." />;
    if (!cardProduct) return <p>Carte introuvable.</p>;
    return (
        <div className="container mx-auto p-4 space-y-4">
            <h1 className="text-3xl font-bold dark:text-white">
                {cardProduct.booster ? "Booster" : "Carte"} {cardProduct.name}
            </h1>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Infos carte */}
                <div className="space-y-2">
                    <p><strong>Référence :</strong> {cardProduct.reference}</p>
                    <p><strong>License :</strong> {cardProduct.license_name}</p>
                    <p><strong>Collection :</strong> {cardProduct.collection?.collection_name}</p>
                    <p><strong>Extension :</strong> {cardProduct.extension?.extension_name || "Aucune"}</p>
                    <p><strong>Rareté :</strong> {cardProduct.rarity?.rarity_name}</p>
                    <p><strong>Langue :</strong> {cardProduct.language?.language_name}</p>
                    <p><strong>Prix d'achat :</strong> {cardProduct.buy} €
                        <button onClick={() => setShowPriceModal(true)} className="text-blue-500 underline bg-gray-100 ml-2 px-2 py-1 rounded hover:bg-gray-200">
                            <FontAwesomeIcon icon={faPenToSquare} />
                        </button>
                    </p>
                    <table>
                        <thead>
                            <tr>
                                <th className="text-left"><strong>Quantités</strong></th>
                                <th className="text-right"><strong>Côtation</strong></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cardProduct.qualities?.map(q => (
                                <tr key={q.id} className="list-disc pl-5">
                                    <td className="w-1/2 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-left">{q.quality_card_name} : {q.pivot.quantity}</td>
                                    <td className="w-1/2 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-right">{`${((Number(cardProduct.sell) || 0) * ((-20 * q.id + 120) / 100)).toFixed(2)} €`}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p><strong>Commentaire :</strong> {cardProduct.commentary}</p>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <button onClick={() => setShowReplenishmentModal(true)} className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 w-full">Réapprovisionnement</button>
                        <button onClick={() => setShowSellModal(true)} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 w-full">Vendre des cartes</button>
                    </div>

                    {cardProduct.links?.length > 0 && (
                        <div className="space-y-2 mt-4 bg-gray-100 text-center p-2 rounded">
                            <ul className="list-disc pl-5">
                                {cardProduct.links.map(link => (
                                    <li key={link.id} className="list-none">
                                        <a href={link.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                            {link.type_link?.type_link_name || "Lien"}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex gap-2">
                        <button onClick={() => navigate(`/edit-unity-card/${cardProduct.id}`)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Modifier la carte</button>
                        <button onClick={() => setShowDuplicateModal(true)} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Dupliquer la carte</button>
                    </div>
                </div>

                {/* Image */}
                <div className="ml-auto">
                    {cardProduct.picture ? (
                        <img src={`${API_ROUTES.IMG}${cardProduct.picture}`} alt={cardProduct.name} className="max-h-[500px] border rounded" />
                    ) : (
                        <FontAwesomeIcon icon={faImage} className="w-24 h-24 text-gray-400 mb-2" />
                    )}
                    <input type="file" accept="image/*" ref={fileInputRef} onChange={handleUploadImage} className="hidden" />
                    <button onClick={openFilePicker} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 w-full">
                        {cardProduct.picture ? "Modifier l’image" : "Ajouter une image"}
                    </button>
                </div>
            </div>

            {/* Modal Prix d'achat */}
            {showPriceModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <form onSubmit={updateBuyPrice} className="dark:bg-gray-700 bg-gray-100 p-6 rounded shadow-lg w-96 space-y-4">
                        <h2 className="text-xl font-bold">Modifier le prix d’achat</h2>
                        <p>Nom : {cardProduct.name}</p>
                        <p>Ancien prix d’achat : {cardProduct.buy} €</p>
                        <label className="block">
                            Nouveau prix (€) :
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={newBuyPrice}
                                onChange={e => setNewBuyPrice(e.target.value)}
                                className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                required
                            />
                        </label>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowPriceModal(false)} className="px-4 py-2 dark:bg-gray-500 bg-gray-300 rounded">Annuler</button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Mettre à jour</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Modal Vente */}
            {showSellModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <form onSubmit={sellCards} className="dark:bg-gray-700 bg-gray-100 p-6 rounded shadow-lg w-96 space-y-4">
                        <h2 className="text-xl font-bold">Vendre des cartes</h2>
                        <p>Nom : {cardProduct.name}</p>

                        <label className="block">
                            Qualité :
                            <select
                                value={sellQualityId}
                                onChange={e => setSellQualityId(e.target.value)}
                                className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                required
                            >
                                <option value="">-- Sélectionnez une qualité --</option>
                                {cardProduct.qualities.map(q => (
                                    <option key={q.id} value={q.id}>
                                        {q.quality_card_name} ({q.pivot.quantity})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            Quantité à vendre :
                            <input
                                type="number"
                                min="1"
                                max={sellQualityId ? cardProduct.qualities.find(q => q.id === parseInt(sellQualityId))?.pivot.quantity || 0 : 0}
                                value={sellQty}
                                onChange={e => setSellQty(parseInt(e.target.value) || 0)}
                                className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                required
                            />
                        </label>

                        <label className="block">
                            Prix unitaire (€) :
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={sellPrice}
                                onChange={e => setSellPrice(e.target.value)}
                                className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                required
                            />
                        </label>

                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowSellModal(false)} className="px-4 py-2 dark:bg-gray-500 bg-gray-300 rounded">Annuler</button>
                            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded">Vendre</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Modal Réapprovisionnement */}
            {showReplenishmentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <form onSubmit={replenishmentCards} className="dark:bg-gray-700 bg-gray-100 p-6 rounded shadow-lg w-96 space-y-4">
                        <h2 className="text-xl font-bold">Réapprovisionnement</h2>
                        <p>Nom : {cardProduct.name}</p>

                        <label className="block">
                            Qualité :
                            <select
                                value={replenishQualityId}
                                onChange={e => setReplenishQualityId(e.target.value)}
                                className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                required
                            >
                                <option value="">-- Sélectionnez une qualité --</option>
                                {cardProduct.qualities.map(q => (
                                    <option key={q.id} value={q.id}>
                                        {q.quality_card_name} ({q.pivot.quantity})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            Quantité à ajouter :
                            <input
                                type="number"
                                min="0"
                                value={replenishQty}
                                onChange={e => setReplenishQty(parseInt(e.target.value) || 0)}
                                className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                required
                            />
                        </label>

                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowReplenishmentModal(false)} className="px-4 py-2 dark:bg-gray-500 bg-gray-300 rounded">Annuler</button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Ajouter</button>
                        </div>
                    </form>
                </div>
            )}
            {/* Modal Duplication */}
            {showDuplicateModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 overflow-auto p-4">
                    <form onSubmit={handleDupSubmit} className="bg-white dark:bg-gray-700 p-6 rounded w-full max-w-3xl space-y-4">
                        <h2 className="text-xl font-bold">Dupliquer la carte</h2>

                        <div className="grid grid-cols-2 gap-4">
                            {/* Licence */}
                            <div>
                                <label>Licence :</label>
                                <select
                                    value={dupForm.license_card_id}
                                    onChange={e => setSelectedLicense(e.target.value)}
                                    className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                    required
                                >
                                    <option value="">-- Sélectionnez une licence --</option>
                                    {licenses.map(l => <option key={l.id} value={l.id}>{l.license_card}</option>)}
                                </select>
                            </div>

                            {/* Collection */}
                            <div>
                                <label>Collection :</label>
                                <select
                                    name="collection_id"
                                    value={dupForm.collection_id}
                                    onChange={handleDupChange}
                                    className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                    required
                                >
                                    <option value="">-- Sélectionnez une collection --</option>
                                    {filteredCollections.map(c => <option key={c.id} value={c.id}>{c.collection_name}</option>)}
                                </select>
                            </div>

                            {/* Extension */}
                            <div>
                                <label>Extension :</label>
                                <select
                                    name="extension_id"
                                    value={dupForm.extension_id}
                                    onChange={handleDupChange}
                                    className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                >
                                    <option value="">-- Sélectionnez une extension --</option>
                                    {filteredExtensions.map(ext => <option key={ext.id} value={ext.id}>{ext.extension_name}</option>)}
                                </select>
                            </div>

                            {/* Langue */}
                            <div>
                                <label>Langue :</label>
                                <select
                                    name="language_id"
                                    value={dupForm.language_id}
                                    onChange={handleDupChange}
                                    className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                                    required
                                >
                                    <option value="">-- Sélectionnez une langue --</option>
                                    {languages.map(l => <option key={l.id} value={l.id}>{l.language_name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label>Commentaire :</label>
                            <textarea name="commentary" value={dupForm.commentary} onChange={handleDupChange} rows="4" className="border p-2 w-full rounded dark:bg-gray-700 bg-white" />
                        </div>

                        {/* Type et rareté */}
                        <div className="flex gap-4 mt-2">
                            <button type="button" onClick={() => handleDupChange({ target: { name: "booster", value: "false", type: "radio" } })} className={`flex-1 p-3 rounded border ${!dupForm.booster ? "bg-blue-500 text-white" : "bg-white text-gray-700"}`}>Carte unique</button>
                            <button type="button" onClick={() => handleDupChange({ target: { name: "booster", value: "true", type: "radio" } })} className={`flex-1 p-3 rounded border ${dupForm.booster ? "bg-blue-500 text-white" : "bg-white text-gray-700"}`}>Booster</button>
                        </div>

                        {!dupForm.booster && (
                            <div>
                                <label>Rareté :</label>
                                <select name="rarity_card_id" value={dupForm.rarity_card_id} onChange={handleDupChange} className="border p-2 w-full rounded dark:bg-gray-700 bg-white" required>
                                    <option value="">-- Sélectionnez une rareté --</option>
                                    {rarities.map(r => <option key={r.id} value={r.id}>{r.rarity_name}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Nom, Référence, Prix, Commentaire */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label>Nom :</label>
                                <input type="text" name="name" value={dupForm.name} onChange={handleDupChange} className="border p-2 w-full rounded dark:bg-gray-700 bg-white" required />
                            </div>
                            <div>
                                <label>Référence :</label>
                                <input type="text" name="reference" value={dupForm.reference} onChange={handleDupChange} className="border p-2 w-full rounded dark:bg-gray-700 bg-white" required />
                            </div>
                            <div>
                                <label>Prix d'achat (€) :</label>
                                <input type="number" name="buy" step="0.01" value={dupForm.buy} onChange={handleDupChange} className="border p-2 w-full rounded dark:bg-gray-700 bg-white" />
                            </div>
                            <div>
                                <label>Côtation (€) :</label>
                                <input type="number" name="sell" step="0.01" value={dupForm.sell} onChange={handleDupChange} className="border p-2 w-full rounded dark:bg-gray-700 bg-white" />
                            </div>
                            <div>
                            </div>
                            <div className="flex items-center justify-between">
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Near Mint : ${(Number(dupForm.sell) || 0).toFixed(2)} €`}
                                    onChange={handleDupChange}
                                    className="w-1/2 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Good : ${((Number(dupForm.sell) || 0) * 0.8).toFixed(2)} €`}
                                    onChange={handleDupChange}
                                    className="w-1/2 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                            </div>
                            <div>
                            </div>
                            <div className="flex items-center justify-between">
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Played : ${((Number(dupForm.sell) || 0) * 0.6).toFixed(2)} €`}
                                    onChange={handleDupChange}
                                    className="w-1/2 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                                <input
                                    type="text"
                                    name="sell"
                                    value={`Poor : ${((Number(dupForm.sell) || 0) * 0.4).toFixed(2)} €`}
                                    onChange={handleDupChange}
                                    className="w-1/2 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 bg-white text-center"
                                    readOnly
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setShowDuplicateModal(false)} className="bg-gray-500 text-white px-4 py-2 rounded">Annuler</button>
                            <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded">Dupliquer</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default ViewUnityCard;